import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3'
};

const EditTag = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  
  const [formData, setFormData] = useState({
    info_tag: "",
    statut_tag: "actif",
    qr_code: "",
    nfc_uid: "",
    type_tag: ""
  });

  useEffect(() => {
    if (id) {
      fetchTag();
    }
  }, [id]);

  const fetchTag = async () => {
    setLoading(true);
    setError("");
    setValidationErrors([]);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/tags/get_tags.php?id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data?.success) {
        const tagData = response.data.data;
        setFormData({
          info_tag: tagData.info_tag || "",
          statut_tag: tagData.statut_tag || "actif",
          qr_code: tagData.qr_code || "",
          nfc_uid: tagData.nfc_uid || "",
          type_tag: tagData.type_tag || ""
        });
      } else {
        setError(response.data?.message || "Tag non trouvé.");
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    if (err.response?.status === 401) {
      setError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      // Vérifier si c'est une erreur d'abonnement
      if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
        setError(
          <div>
            <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
              {err.response.data.message}
            </div>
            <div className="d-flex gap-2 justify-content-center">
              <a 
                href="/abonnements/create"
                className="btn fw-semibold"
                style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
              >
                <i className="bi bi-box-arrow-up-right"></i>
                Souscrire à un abonnement
              </a>
              <button
                className="btn fw-semibold"
                onClick={() => setError("")}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
              >
                Fermer
              </button>
            </div>
          </div>
        );
      } else {
        setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
      }
    } else if (err.response?.status === 404) {
      setError("Tag non trouvé.");
    } else if (err.response?.status === 400) {
      setError("ID tag invalide.");
    } else if (err.response?.status === 500) {
      setError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setError("Erreur technique lors du chargement du tag.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    setError("");
    setValidationErrors([]);
    setSuccessMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const errors = [];
    if (!formData.info_tag.trim()) {
      errors.push("L'information du tag est obligatoire.");
    }
    
    if (formData.info_tag.trim().length > 1000) {
      errors.push("Les informations du tag ne peuvent pas dépasser 1000 caractères.");
    }
    
    if (formData.qr_code && formData.qr_code.length > 255) {
      errors.push("Le code QR ne peut pas dépasser 255 caractères.");
    }
    
    if (formData.nfc_uid && formData.nfc_uid.length > 255) {
      errors.push("L'UID NFC ne peut pas dépasser 255 caractères.");
    }
    
    if (!formData.qr_code && !formData.nfc_uid) {
      errors.push("Le tag doit avoir au moins un identifiant (QR Code ou NFC UID).");
    }
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setSaving(true);
    setError("");
    setValidationErrors([]);
    setSuccessMessage("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      // Préparer les données à envoyer
      const dataToSend = {
        id_tag: parseInt(id),
        info_tag: formData.info_tag.trim(),
        statut_tag: formData.statut_tag,
        qr_code: formData.qr_code.trim() || null,
        nfc_uid: formData.nfc_uid.trim() || null
      };

      const response = await axios.post(`${API_BASE_URL}/tags/update_tags.php`, dataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.success) {
        setSuccessMessage(response.data.message || "Tag modifié avec succès !");
        
        setTimeout(() => {
          navigate("/tags");
        }, 2000);
      } else {
        setError(response.data?.message || "Erreur lors de la modification du tag.");
      }
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitError = (err) => {
    if (err.response?.status === 401) {
      setError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      // Vérifier si c'est une erreur d'abonnement
      if (err.response.data?.code === 'ABONNEMENT_REQUIS') {
        setError(
          <div>
            <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
              {err.response.data.message}
            </div>
            <div className="d-flex gap-2 justify-content-center">
              <a 
                href="/abonnements/create"
                className="btn fw-semibold"
                style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
              >
                <i className="bi bi-box-arrow-up-right"></i>
                Souscrire à un abonnement
              </a>
              <button
                className="btn fw-semibold"
                onClick={() => setError("")}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
              >
                Fermer
              </button>
            </div>
          </div>
        );
      } else {
        setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
      }
    } else if (err.response?.status === 400) {
      if (err.response.data?.errors) {
        setValidationErrors(errors => [...errors, ...err.response.data.errors]);
      } else {
        setError(err.response.data?.message || "Données invalides.");
      }
    } else if (err.response?.status === 404) {
      setError("Tag non trouvé.");
    } else if (err.response?.status === 409) {
      setError(err.response.data?.message || "Un autre tag avec ces informations existe déjà.");
    } else if (err.response?.status === 500) {
      setError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setError("Erreur technique lors de la modification du tag.");
    }
  };

  const handleCancel = () => {
    navigate("/tags");
  };

  // Fonction pour obtenir les informations du type de tag
  const getTagTypeInfo = (typeTag) => {
    const typeStr = String(typeTag).toLowerCase().trim();
    switch(typeStr) {
      case 'qr':
      case '1':
      case 'qrcode':
        return {
          label: 'QR Code',
          icon: 'bi-qr-code',
          color: '#4CAF50'
        };
      case 'nfc':
      case '2':
        return {
          label: 'NFC',
          icon: 'bi-nfc',
          color: '#2196F3'
        };
      default:
        return {
          label: 'Inconnu',
          icon: 'bi-tag',
          color: '#9E9E9E'
        };
    }
  };

  const tagTypeInfo = getTagTypeInfo(formData.type_tag);

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le Tag</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/tags">Tags</Link>
              </li>
              <li className="breadcrumb-item active">Chargement...</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement du tag...</p>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Modifier le Tag</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/tags">Tags</Link>
            </li>
            <li className="breadcrumb-item active">Modifier</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-8">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title mb-4" style={{ color: COLORS.primary }}>
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                    width: '30px',
                    height: '30px',
                    backgroundColor: '#E3F2FD',
                    color: COLORS.primary
                  }}>
                    <i className="bi bi-pencil"></i>
                  </div>
                  Modifier les informations du tag
                </h5>
                
                <div className="alert alert-info mb-4" style={{ 
                  backgroundColor: '#E3F2FD',
                  borderColor: COLORS.info,
                  color: COLORS.darkText,
                  borderRadius: '8px'
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-info-circle me-2"></i>
                    <div className="flex-grow-1">
                      <strong>Type de tag :</strong> {tagTypeInfo.label}
                      <br />
                      <small>Vous pouvez modifier les identifiants ci-dessous si nécessaire.</small>
                    </div>
                  </div>
                </div>
                
                {error && (
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`,
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                      <div className="flex-grow-1">
                        {error}
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError("")}
                      ></button>
                    </div>
                  </div>
                )}
                
                {validationErrors.length > 0 && (
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#fff3e0',
                    borderColor: COLORS.warning,
                    color: '#f57c00',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.warning}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">
                        <strong>Erreurs de validation :</strong>
                        <ul className="mb-0 mt-2">
                          {validationErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                          ))}
                        </ul>
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setValidationErrors([])}
                      ></button>
                    </div>
                  </div>
                )}
                
                {successMessage && (
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2"></i>
                      <div className="flex-grow-1">
                        {successMessage}
                        <div className="mt-2">
                          <small>Redirection vers la liste des tags...</small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Informations principales */}
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <label htmlFor="info_tag" className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                        Description du tag <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className={`form-control ${error && !formData.info_tag.trim() ? 'is-invalid' : ''}`}
                        id="info_tag"
                        name="info_tag"
                        value={formData.info_tag}
                        onChange={handleChange}
                        placeholder="Entrez la description du tag..."
                        rows="4"
                        required
                        disabled={saving || successMessage}
                        style={{
                          borderRadius: '8px',
                          border: `1px solid ${COLORS.lightBg}`,
                          padding: '12px',
                          fontSize: '0.95rem',
                          resize: 'vertical'
                        }}
                      />
                      <div className="form-text mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        Description détaillée du tag (max 1000 caractères). 
                        {formData.info_tag.length > 0 && (
                          <span className="ms-2">
                            {formData.info_tag.length}/1000 caractères
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* QR Code */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label htmlFor="qr_code" className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                        Code QR
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#E8F5E9',
                          borderColor: COLORS.lightBg,
                          color: '#4CAF50'
                        }}>
                          <i className="bi bi-qr-code"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          id="qr_code"
                          name="qr_code"
                          value={formData.qr_code}
                          onChange={handleChange}
                          placeholder="Saisir ou modifier le code QR"
                          disabled={saving || successMessage}
                          style={{
                            borderRadius: '0 8px 8px 0',
                            border: `1px solid ${COLORS.lightBg}`,
                            padding: '10px 12px',
                            fontSize: '0.95rem'
                          }}
                        />
                      </div>
                      <div className="form-text mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        Code QR unique pour ce tag (max 255 caractères)
                      </div>
                    </div>

                    {/* NFC UID */}
                    <div className="col-md-6">
                      <label htmlFor="nfc_uid" className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                        UID NFC
                      </label>
                      <div className="input-group">
                        <span className="input-group-text" style={{ 
                          backgroundColor: '#E3F2FD',
                          borderColor: COLORS.lightBg,
                          color: '#2196F3'
                        }}>
                          <i className="bi bi-nfc"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          id="nfc_uid"
                          name="nfc_uid"
                          value={formData.nfc_uid}
                          onChange={handleChange}
                          placeholder="Saisir ou modifier l'UID NFC"
                          disabled={saving || successMessage}
                          style={{
                            borderRadius: '0 8px 8px 0',
                            border: `1px solid ${COLORS.lightBg}`,
                            padding: '10px 12px',
                            fontSize: '0.95rem'
                          }}
                        />
                      </div>
                      <div className="form-text mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        Identifiant unique NFC (max 255 caractères)
                      </div>
                    </div>
                  </div>

                  {/* Statut */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <label htmlFor="statut_tag" className="form-label fw-semibold" style={{ color: COLORS.darkText }}>
                        Statut <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="statut_tag"
                        name="statut_tag"
                        value={formData.statut_tag}
                        onChange={handleChange}
                        disabled={saving || successMessage}
                        required
                        style={{
                          borderRadius: '8px',
                          border: `1px solid ${COLORS.lightBg}`,
                          padding: '10px 12px',
                          fontSize: '0.95rem'
                        }}
                      >
                        <option value="actif">Actif</option>
                        <option value="inactif">Inactif</option>
                      </select>
                      <div className="form-text mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        <i className="bi bi-info-circle me-1"></i>
                        Détermine si le tag est utilisable ou non
                      </div>
                    </div>

                    {/* Informations système */}
                    <div className="col-md-6">
                      <div className="p-4 rounded" style={{ 
                        backgroundColor: '#F8F9FA',
                        borderLeft: `4px solid ${COLORS.primary}`,
                        height: '100%'
                      }}>
                        <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary }}>
                          <i className="bi bi-info-circle me-2"></i>
                          Informations système
                        </h6>
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1" style={{ fontSize: '0.8rem' }}>
                            <strong>ID Tag</strong>
                          </small>
                          <div className="d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                              width: '30px',
                              height: '30px',
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              fontSize: '0.8rem'
                            }}>
                              <i className="bi bi-hash"></i>
                            </div>
                            <span style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              {id}
                            </span>
                          </div>
                        </div>
                        <div>
                          <small className="text-muted d-block mb-1" style={{ fontSize: '0.8rem' }}>
                            <strong>Type de tag</strong>
                          </small>
                          <div className="d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                              width: '30px',
                              height: '30px',
                              backgroundColor: tagTypeInfo.color + '20',
                              color: tagTypeInfo.color,
                              fontSize: '0.8rem'
                            }}>
                              <i className={`bi ${tagTypeInfo.icon}`}></i>
                            </div>
                            <span style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              {tagTypeInfo.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Boutons d'action */}
                  <div className="mt-5 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                    <div className="d-flex justify-content-end gap-3">
                      <button
                        type="button"
                        className="btn fw-semibold"
                        onClick={handleCancel}
                        disabled={saving}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#E3F2FD';
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="btn fw-semibold"
                        disabled={saving || successMessage}
                        style={{
                          color: COLORS.white,
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                          borderRadius: '8px',
                          padding: '10px 24px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!saving && !successMessage) {
                            e.target.style.backgroundColor = COLORS.secondary;
                            e.target.style.borderColor = COLORS.secondary;
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!saving && !successMessage) {
                            e.target.style.backgroundColor = COLORS.primary;
                            e.target.style.borderColor = COLORS.primary;
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Enregistrement...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Enregistrer les modifications
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .alert,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .badge,
        .card {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-end {
            flex-direction: column;
            gap: 12px;
          }
          
          .d-flex.justify-content-end > .btn {
            width: 100%;
          }
          
          .row.mb-4 .col-md-6 {
            margin-bottom: 16px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default EditTag;